import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Hash,
  Lock,
  Users,
  Bell,
  CheckCheck,
  Phone,
  Video,
  AlertTriangle,
  Sliders,
  Sparkles,
  ExternalLink,
  Trash2
} from 'lucide-react';

import { useChat } from '../../context/ChatContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useCall } from '../../context/CallContext';
import { useGroupCall } from '../../context/GroupCallContext';
import { useEmergency } from '../../context/EmergencyContext';
import { useLanguage } from '../../hooks/useLanguage';
import { api, ws } from '../../lib/api';
import { topics } from '@sahaj/websocket';
import { ringtonePlayer } from '../../utils/ringtone';
import { NotificationSettingsModal } from '../modals/NotificationSettingsModal';
import { ConfirmDeleteModal } from '../modals/ConfirmDeleteModal';

import type { Notification, CallType, WebSocketEvent, UserNotificationPreferences } from '@sahaj/types';
import { formatRelativeTime } from '@sahaj/utils';
import { UserAvatar } from '../common/UserAvatar';

const getNotificationContent = (
  notification: Notification
): { title: string; message?: string | undefined } => {
  const actorName = (notification.metadata?.actor_name as string) || notification.actor?.displayName || 'System';
  const channelName = notification.metadata?.channel_name as string | undefined;
  const preview = (notification.metadata?.preview_text as string) || undefined;

  switch (notification.type) {
    case 'MENTION':
      return {
        title: `${actorName} mentioned you in #${channelName || 'channel'}`,
        message: preview || 'You were mentioned in a message',
      };

    case 'REPLY':
      return {
        title: `${actorName} replied to your thread in #${channelName || 'channel'}`,
        message: preview || 'Someone replied to your thread',
      };

    case 'DM':
      return {
        title: `New message from ${actorName}`,
        message: preview,
      };

    case 'MEMBER_ADDED':
      return {
        title: `Added to #${channelName || 'channel'}`,
        message: `${actorName} added you to the channel`,
      };

    case 'WORKSPACE_INVITED':
      return {
        title: 'Workspace Invitation',
        message: `You joined ${(notification.metadata?.workspace_name as string) || 'the workspace'}`,
      };

    case 'EMERGENCY_ALERT':
      return {
        title: '⚠️ Emergency Alert',
        message: `${actorName}: ${preview || 'Emergency Alert'}`,
      };

    case 'MISSED_CALL':
      return {
        title: `📞 Missed Call from ${actorName}`,
        message: preview || `Missed ${notification.metadata?.call_type || 'voice'} call`,
      };

    default:
      return {
        title: `Notification from ${actorName}`,
        message: preview,
      };
  }
};

// BroadcastChannel key for multi-tab read-state sync
const NOTIF_CHANNEL_NAME = 'sahaj_notifications';

export const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const { activeChat, activeThread, deleteChannel } = useChat();
  const { members, currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { initiateCall } = useCall();
  const { initiateGroupCall } = useGroupCall();
  const { openSendModal } = useEmergency();
  const { t } = useLanguage();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userPrefs, setUserPrefs] = useState<UserNotificationPreferences | null>(null);

  const lastChimeRef = useRef<number>(0);
  const broadcastRef = useRef<BroadcastChannel | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentMember = members.find((m) => m.user?.id === user?.id);
  const isWorkspaceAdminOrOwner =
    currentMember?.role === 'ADMIN' ||
    currentMember?.role === 'OWNER' ||
    currentWorkspace?.ownerId === user?.id;

  const canDeleteCurrentChannel =
    activeChat?.type === 'channel' &&
    (activeChat.channel.createdBy === user?.id || isWorkspaceAdminOrOwner);

  const isQuietHoursActive = (prefs: UserNotificationPreferences | null): boolean => {
    if (!prefs || !prefs.quietHoursEnabled || !prefs.quietHoursStart || !prefs.quietHoursEnd) {
      return false;
    }
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const startParts = prefs.quietHoursStart.split(':').map((v) => parseInt(v, 10));
    const endParts = prefs.quietHoursEnd.split(':').map((v) => parseInt(v, 10));
    const startH = startParts[0] ?? 0;
    const startM = startParts[1] ?? 0;
    const endH = endParts[0] ?? 0;
    const endM = endParts[1] ?? 0;

    if (isNaN(startH) || isNaN(startM) || isNaN(endH) || isNaN(endM)) return false;

    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (startMinutes < endMinutes) {
      return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    } else {
      return currentMinutes >= startMinutes || currentMinutes < endMinutes;
    }
  };

  const checkIsActiveContext = (incoming: Notification): boolean => {
    if (!document.hasFocus()) return false;
    const meta = incoming.metadata;
    if (!meta) return false;

    if (incoming.type === 'DM') {
      const convId = meta.conversation_id as string | undefined;
      return activeChat?.type === 'dm' && activeChat.conversation.id === convId;
    }

    const chanId = meta.channel_id as string | undefined;
    if (chanId && activeChat?.type === 'channel' && activeChat.channel.id === chanId) {
      const threadId = meta.thread_id as string | undefined;
      if (threadId && activeThread) {
        return activeThread.id === threadId;
      }
      return true;
    }

    return false;
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  // Load preferences
  useEffect(() => {
    if (!currentWorkspace) return;
    api.notifications.getPreferences(currentWorkspace.id)
      .then(setUserPrefs)
      .catch(() => {});
  }, [currentWorkspace]);

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    if (!currentWorkspace) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      const [listRes, count] = await Promise.all([
        api.notifications.list(currentWorkspace.id, { limit: 20 }),
        api.notifications.unreadCount(currentWorkspace.id),
      ]);
      setNotifications(listRes.data || []);
      setUnreadCount(typeof count === 'number' ? count : (count as any)?.count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Web WS reconnect catch-up listener
  useEffect(() => {
    if (!currentWorkspace) return;

    const unsubWsState = ws.onStateChange((state) => {
      if (state === 'CONNECTED') {
        fetchNotifications();
      }
    });

    return () => {
      unsubWsState();
    };
  }, [currentWorkspace, fetchNotifications]);

  /**
   * STOMP: subscribe /user/queue/notifications for real-time delivery and multi-device read sync.
   * Performs active-context sound suppression and auto-marks read if chat is focused.
   */
  useEffect(() => {
    if (!currentWorkspace) return;

    const unsub = ws.subscribe(topics.notifications(), (event: WebSocketEvent) => {
      if (event.eventType === 'NOTIFICATION_CREATED' && event.payload) {
        const incoming = event.payload as Notification;
        const isActiveContext = checkIsActiveContext(incoming);

        if (isActiveContext) {
          // Auto mark as read immediately in backend & local state
          api.notifications.markRead(incoming.id).catch(() => {});
          setNotifications((prev) => {
            if (prev.some((n) => n.id === incoming.id)) {
              return prev.map((n) => (n.id === incoming.id ? { ...n, isRead: true } : n));
            }
            return [{ ...incoming, isRead: true }, ...prev];
          });
          return;
        }

        // Context is NOT active -> add unread, increment count, evaluate sound
        setNotifications((prev) => {
          if (prev.some((n) => n.id === incoming.id)) return prev;
          return [incoming, ...prev];
        });

        setUnreadCount((c) => c + 1);

        // Sound evaluation: check sound toggle and quiet hours
        const soundAllowed = (userPrefs ? userPrefs.soundEnabled : true) && !isQuietHoursActive(userPrefs);
        const now = Date.now();
        if (soundAllowed && now - lastChimeRef.current > 2000) {
          lastChimeRef.current = now;
          ringtonePlayer.playNotificationChime();
        }

        // Desktop Notification for background tabs
        if (
          document.hidden &&
          userPrefs?.desktopPushEnabled &&
          typeof window !== 'undefined' &&
          'Notification' in window &&
          Notification.permission === 'granted'
        ) {
          const content = getNotificationContent(incoming);
          const desktopNotif = new window.Notification(content.title, {
            body: content.message || '',
            icon: '/favicon.ico',
          });
          desktopNotif.onclick = () => {
            window.focus();
            const targetRoute = incoming.metadata?.target_route as string | undefined;
            if (targetRoute) navigate(targetRoute);
          };
        }
      } else if (event.eventType === 'READ_STATE_UPDATED' && event.payload) {
        const payload = event.payload as { scope?: string; notificationId?: string; isRead?: boolean };
        if (payload.scope === 'ALL') {
          setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
          setUnreadCount(0);
        } else if (payload.notificationId) {
          setNotifications((prev) => {
            const match = prev.find((n) => n.id === payload.notificationId);
            if (match && !match.isRead) {
              setUnreadCount((c) => Math.max(0, c - 1));
            }
            return prev.map((n) => (n.id === payload.notificationId ? { ...n, isRead: true } : n));
          });
        }
      }
    });

    return () => unsub();
  }, [currentWorkspace, activeChat, activeThread, userPrefs, navigate]);

  // BroadcastChannel multi-tab sync
  useEffect(() => {
    const channel = new BroadcastChannel(NOTIF_CHANNEL_NAME);
    broadcastRef.current = channel;

    channel.onmessage = (event: MessageEvent) => {
      const { type, id } = event.data as { type: string; id?: string };
      if (type === 'ALL_READ') {
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      } else if (type === 'ONE_READ' && id) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    };

    return () => channel.close();
  }, []);

  const markAllRead = async () => {
    if (!currentWorkspace) return;
    try {
      await api.notifications.markAllRead(currentWorkspace.id);
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      broadcastRef.current?.postMessage({ type: 'ALL_READ' });
    } catch (err) {
      console.error('Failed to mark notifications read', err);
    }
  };

  const handleNotificationClick = async (notif: Notification) => {
    if (!notif.isRead) {
      try {
        await api.notifications.markRead(notif.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
        broadcastRef.current?.postMessage({ type: 'ONE_READ', id: notif.id });
      } catch (err) {
        console.error('Failed to mark notification read', err);
      }
    }

    setShowNotifications(false);

    // Deep link navigation
    const meta = notif.metadata;
    if (meta?.target_route) {
      navigate(meta.target_route as string);
      return;
    }

    if (notif.type === 'DM' && notif.referenceId) {
      navigate(`/workspace/${currentWorkspace?.id}/dm/${notif.referenceId}`);
    } else if (notif.type === 'MENTION' || notif.type === 'REPLY' || notif.type === 'MEMBER_ADDED') {
      if (meta?.channel_id) {
        navigate(`/workspace/${currentWorkspace?.id}/channel/${meta.channel_id}`);
      }
    } else if (notif.type === 'WORKSPACE_INVITED') {
      navigate(`/workspace/${currentWorkspace?.id}`);
    }
  };

  const handleStartCall = (callType: CallType) => {
    if (!activeChat || activeChat.type !== 'dm' || !currentWorkspace || !user) return;
    const other = activeChat.conversation.members.find(
      (m) => m.id.toLowerCase() !== user.id.toLowerCase()
    );
    if (!other) return;
    initiateCall({
      workspaceId: currentWorkspace.id,
      conversationId: activeChat.conversation.id,
      recipientId: other.id,
      recipientName: other.displayName,
      recipientAvatar: other.avatarUrl ?? undefined,
      callType,
    });
  };

  const handleStartGroupCall = (callType: CallType) => {
    if (!activeChat || activeChat.type !== 'channel' || !currentWorkspace) return;
    initiateGroupCall({
      workspaceId: currentWorkspace.id,
      channelId: activeChat.channel.id,
      callType,
    });
  };

  return (
    <header className="h-14 border-b border-border bg-card/60 backdrop-blur-md flex items-center justify-between px-4 z-20 select-none shadow-[0_1px_3px_0_rgba(0,0,0,0.02)]">
      {/* Settings Modal */}
      {currentWorkspace && (
        <NotificationSettingsModal
          workspaceId={currentWorkspace.id}
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
        />
      )}

      {/* Left: Chat Title & Context */}
      <div className="flex items-center gap-3 min-w-0">
        {activeChat?.type === 'channel' && (
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
              {activeChat.channel.isPrivate ? <Lock className="h-4 w-4" /> : <Hash className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <span className="font-bold text-sm text-foreground truncate block leading-tight">
                {activeChat.channel.name}
              </span>
              {activeChat.channel.description && (
                <span className="text-xs text-muted-foreground truncate block">
                  {activeChat.channel.description}
                </span>
              )}
            </div>
          </div>
        )}

        {activeChat?.type === 'dm' && (
          <div className="flex items-center gap-2.5 min-w-0">
            {(() => {
              const other = activeChat.conversation.members.find(m => m.id !== user?.id) || activeChat.conversation.members[0];
              return (
                <>
                  <UserAvatar
                    userId={other?.id}
                    displayName={other?.displayName}
                    avatarUrl={other?.avatarUrl}
                    size="sm"
                    className="shadow-xs shrink-0"
                  />
                  <div className="min-w-0">
                    <span className="font-bold text-sm text-foreground truncate block leading-tight">
                      {other?.displayName}
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate block">
                      {t('common.directMessage')}
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Right Toolbar */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Calling buttons for DM or Channel */}
        {(activeChat?.type === 'dm' || activeChat?.type === 'channel') && (
          <div className="flex items-center gap-1 mr-2 pr-2 border-r border-border/60">
            <button
              type="button"
              onClick={() => (activeChat.type === 'dm' ? handleStartCall('AUDIO') : handleStartGroupCall('AUDIO'))}
              className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition"
              title={t('header.startCall')}
            >
              <Phone className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => (activeChat.type === 'dm' ? handleStartCall('VIDEO') : handleStartGroupCall('VIDEO'))}
              className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition"
              title={t('header.startVideoCall')}
            >
              <Video className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Channel Delete Button (Creator or Workspace Admin only) */}
        {canDeleteCurrentChannel && (
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            title={t('modals.deleteChannel')}
            aria-label={t('modals.deleteChannel')}
            className="p-2 rounded-xl hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition mr-1"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}

        {/* Emergency Alert Button */}
        <button
          type="button"
          onClick={openSendModal}
          className="p-2 rounded-xl hover:bg-rose-500/10 text-rose-500 transition mr-1"
          title={t('dm.emergencyAlert')}
        >
          <AlertTriangle className="h-4 w-4" />
        </button>

        {/* Activity Center Link */}
        {currentWorkspace && (
          <button
            type="button"
            onClick={() => navigate(`/workspace/${currentWorkspace.id}/activity`)}
            className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition"
            title={t('navigation.activity')}
          >
            <Sparkles className="h-4 w-4 text-primary" />
          </button>
        )}

        {/* Notification Bell with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowNotifications(!showNotifications)}
            className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition relative"
            title={t('header.notifications')}
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center shadow">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-84 sm:w-96 bg-card border border-border rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/20">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-sm text-foreground">{t('navigation.activity')}</h3>
                  {unreadCount > 0 && (
                    <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-[10px] font-semibold rounded-full">
                      {unreadCount} unread
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={markAllRead}
                      className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1"
                    >
                      <CheckCheck className="h-3.5 w-3.5" /> {t('activity.markAllRead')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowNotifications(false);
                      setShowSettingsModal(true);
                    }}
                    className="p-1 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition"
                    title={t('header.notifications')}
                  >
                    <Sliders className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Notification rows */}
              <div className="max-h-[360px] overflow-y-auto divide-y divide-border/60">
                {notifications.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <Bell className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="text-xs">No notifications yet</p>
                  </div>
                ) : (
                  notifications.map((n) => {
                    const content = getNotificationContent(n);
                    return (
                      <div
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        className={`p-3.5 flex items-start gap-3 transition cursor-pointer hover:bg-muted/40 ${
                          n.isRead ? 'bg-transparent' : 'bg-primary/[0.04]'
                        }`}
                      >
                        <div className="pt-1 w-2 flex-shrink-0">
                          {!n.isRead && (
                            <span className="block w-2 h-2 rounded-full bg-primary animate-pulse" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground leading-snug">
                            {content.title}
                          </p>
                          {content.message && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-normal">
                              {content.message}
                            </p>
                          )}
                          <span className="text-[10px] text-muted-foreground/70 mt-1 block">
                            {formatRelativeTime(n.createdAt)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer View All */}
              {currentWorkspace && (
                <div className="p-2 border-t border-border bg-muted/10 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNotifications(false);
                      navigate(`/workspace/${currentWorkspace.id}/activity`);
                    }}
                    className="w-full py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 rounded-xl transition flex items-center justify-center gap-1.5"
                  >
                    View All in Activity Center <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Member Count */}
        {activeChat?.type === 'channel' && (
          <div className="hidden md:flex items-center gap-1 text-xs font-medium text-muted-foreground bg-muted/40 px-2.5 py-1 rounded-lg border border-border ml-1">
            <Users className="h-3.5 w-3.5" />
            <span>{members.length}</span>
          </div>
        )}
      </div>

      {/* Confirm Delete Channel Modal */}
      {showDeleteModal && activeChat?.type === 'channel' && (
        <ConfirmDeleteModal
          isOpen={true}
          onClose={() => setShowDeleteModal(false)}
          type="channel"
          itemName={activeChat.channel.name}
          onConfirm={async () => {
            await deleteChannel(activeChat.channel.id);
          }}
        />
      )}
    </header>
  );
};